import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Mic, Send, CheckCircle2 } from 'lucide-react';
const topics = ['Healthcare Leadership', 'Healthcare Policy', 'Physician Advocacy', 'Board Governance', 'Strategic Growth', 'Healthcare Transformation', 'Government Relations', 'Healthcare Workforce Development'];
const appearances = [{
  year: '2025',
  event: 'National Healthcare Leadership Summit',
  topic: 'The Future of Physician Advocacy'
}, {
  year: '2024',
  event: 'State Medical Association Annual Conference',
  topic: 'Transforming Association Strategy'
}, {
  year: '2023',
  event: 'Governance & Board Leadership Forum',
  topic: 'Effective Board Stewardship'
}];
export function Speaking() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  useEffect(() => {
    document.title = 'Speaking & Thought Leadership | Michelle McOmber Keynote Speaker';
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setTimeout(() => setStatus('success'), 1000);
  };
  return <div className="w-full bg-white" data-id="element-403">
      <header className="bg-navy-900 text-white py-20" data-id="element-404">
        <div className="max-w-7xl mx-auto px-6" data-id="element-405">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-406">
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-white" data-id="element-407">
              Speaking &amp; Thought Leadership
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl" data-id="element-408">
              Keynotes and conversations on healthcare leadership, policy, and
              transformation.
            </p>
          </motion.div>
        </div>
      </header>

      <section className="py-20" data-id="element-409">
        <div className="max-w-7xl mx-auto px-6" data-id="element-410">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-411">
            <div data-id="element-412">
              <h2 className="text-3xl font-serif font-bold text-navy-900 mb-6" data-id="element-413">
                Insight That Moves Healthcare Forward
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-414">
                Michelle delivers compelling keynotes that blend executive
                experience, policy expertise, and a passion for advancing
                healthcare communities.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-415">
                {topics.map(t => <div key={t} className="flex items-center text-navy-900 font-medium" data-id="element-416">
                    <span className="w-2 h-2 bg-gold rounded-full mr-3" data-id="element-417" />
                    {t}
                  </div>)}
              </div>
            </div>

            <div className="relative" data-id="element-418">
              <div className="aspect-video bg-slate-900 rounded-xl overflow-hidden relative group cursor-pointer shadow-2xl" data-id="element-419">
                <img src="https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&q=80" alt="Michelle McOmber speaking at a healthcare conference" className="w-full h-full object-cover opacity-60" data-id="element-420" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-421">
                  <span className="w-20 h-20 bg-gold rounded-full flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-422">
                    <Play className="w-10 h-10 text-white" data-id="element-423" />
                  </span>
                </div>
                <div className="absolute bottom-6 left-6 text-white" data-id="element-424">
                  <p className="font-bold text-lg" data-id="element-425">Speaking Reel</p>
                  <p className="text-sm text-white/80" data-id="element-426">
                    Highlights from recent keynotes
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Appearances */}
          <div className="mb-24" data-id="element-427">
            <h2 className="text-2xl font-serif font-bold text-navy-900 mb-8 text-center" data-id="element-428">
              Recent Appearances
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-429">
              {appearances.map(a => <div key={a.event} className="border border-slate-200 p-8 rounded-lg hover:border-gold transition-colors" data-id="element-430">
                  <p className="text-gold font-bold mb-2" data-id="element-431">{a.year}</p>
                  <p className="font-bold text-navy-900 text-lg mb-2" data-id="element-432">
                    {a.event}
                  </p>
                  <p className="text-slate-600 text-sm" data-id="element-433">
                    <strong data-id="element-434">Topic:</strong> {a.topic}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Event inquiry form */}
          <div className="max-w-2xl mx-auto bg-slate-50 rounded-xl p-8 md:p-10" data-id="element-435">
            <div className="flex items-center mb-6" data-id="element-436">
              <Mic className="w-8 h-8 text-gold mr-4" data-id="element-437" />
              <h2 className="text-2xl font-serif font-bold text-navy-900" data-id="element-438">
                Event Inquiry
              </h2>
            </div>
            {status === 'success' ? <div className="text-center py-10" data-id="element-439">
                <CheckCircle2 className="w-12 h-12 text-gold mx-auto mb-4" data-id="element-440" />
                <p className="text-navy-900 font-bold text-lg" data-id="element-441">
                  Thank you for your inquiry.
                </p>
                <p className="text-slate-600" data-id="element-442">
                  Michelle&rsquo;s office will respond within two business days.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-443">
                <input required placeholder="Your name" className="w-full border border-slate-200 rounded px-4 py-3" data-id="element-444" />
                <input required placeholder="Organization" className="w-full border border-slate-200 rounded px-4 py-3" data-id="element-445" />
                <input required type="email" placeholder="Email" className="w-full border border-slate-200 rounded px-4 py-3" data-id="element-446" />
                <input placeholder="Event date & location" className="w-full border border-slate-200 rounded px-4 py-3" data-id="element-447" />
                <textarea required placeholder="Tell us about your event" rows={4} className="w-full border border-slate-200 rounded px-4 py-3" data-id="element-448" />
                <button type="submit" disabled={status === 'submitting'} className="w-full bg-navy-900 hover:bg-navy-800 text-white py-3 rounded font-medium transition-colors flex items-center justify-center disabled:opacity-60" data-id="element-449">
                  {status === 'submitting' ? 'Sending…' : 'Submit Inquiry'}
                  <Send className="w-4 h-4 ml-2" data-id="element-450" />
                </button>
              </form>}
          </div>
        </div>
      </section>
    </div>;
}